/**
 * 邮件咨询中介表单
 * 
 * @Author: Focci
 * @Date: 2023-08-16 09:02:38
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-16 09:02:38
 */

import PropTypes from 'prop-types'
import ActionButtons from '@comp/form/ActionButtons'
import useTracking from '@hook/useTracking'
import { useEnquiryInfo } from '@hook/useEnquiryInfo'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { clean, getPhonePrefix } from '@lib/utils'
import ControllerFiled from '@comp/form/ControllerFiled'
import StyledInput from '@comp/form/StyledInput'
import EnquiryOptions from '../agent-card/EnquiryOptions'

function FormLabel({
  label = '',
  required = false
}) {
  return (
    <div className="flex items-center gap-1 mt-4">
      <b>{label}</b>
      {required && <span className="text-red-500">*</span>}
    </div>
  )
}

export default function MailConsultingAgency({
  type = '',
  id = null,
  agentId = '',
  body = '',
  onSuccess = () => {},
  house
}) {
  const te = useTranslations('enquiry')
  const { pushTrackListing, pushTrack } = useTracking()
  
  const { 
    spec, form, loading, userName, userMobile, emailRules, 
    userEmail, reset, setSpec, submitEnquiry,
  } = useEnquiryInfo()

  const handleConfirm = useCallback(async (d) => {
    const param = clean({ 
      ...d, 
      id: type === 'agent' ? agentId : id, 
      agentId, 
      type, 
      spec: spec.join(','), 
      countryCode: getPhonePrefix()
    })

    house && pushTrackListing('click_email_sent', house)
    pushTrack('click_email_sent', { 
      houseId: id || 0, 
      agentId, 
      agencyOfficeId: house?.office_id || house?.office?.id 
    })

    await submitEnquiry(param)
    onSuccess()
  }, [
    id, agentId, type, spec, house,
    pushTrackListing, pushTrack, submitEnquiry, onSuccess
  ])

  return (
    <div className="flex flex-col gap-2 max-w-[30rem] mx-auto">
      <FormLabel label={te('yourName')} required />
      <ControllerFiled
        name="name"
        defaultValue={userName}
        inputProps={{ 
          placeholder: te('yourName'), 
          variant: 'outline' 
        }}
        Input={StyledInput}
        form={form}
        rules={{ required: 'Name is required!' }}
      />
      <FormLabel label={te('contatPhone')} />
      <ControllerFiled
        name="phoneNumber"
        defaultValue={userMobile}
        inputProps={{ 
          placeholder: te('contatPhone'), 
          variant: 'outline' 
        }}
        Input={StyledInput}
        form={form}
      />
      <FormLabel label={te('yourEmail')} required />
      <ControllerFiled
        name="email"
        defaultValue={userEmail}
        inputProps={{ 
          placeholder: te('typeYourEmail'), 
          variant: 'outline' 
        }}
        Input={StyledInput}
        form={form}
        rules={emailRules}
      />
      <EnquiryOptions 
        type={type}
        value={spec}
        onChange={({ value: v }) => setSpec(v)}
        title={<FormLabel label={te('type')} />}
      />
      <FormLabel label={te('yourMessage')} />
      <ControllerFiled
        name="body"
        defaultValue={body}
        inputProps={{ 
          placeholder: te('yourMessage'), 
          variant: 'outline',
          multiline: true,
          sx: {
            '& .MuiInputBase-input': { minHeight: '3rem' }
          }
        }}
        Input={StyledInput}
        form={form}
      />
      <div className="my-6 w-[20rem] max-w-full mx-auto">
        <ActionButtons
          loading={loading}
          onConfirm={form.handleSubmit(handleConfirm)}
          onCancel={reset}
          confirmLabel={te('sendEmail')}
        />
      </div>
    </div>
  )
}

MailConsultingAgency.propTypes = {
  type: PropTypes.string,
  id: PropTypes.number,
  agentId: PropTypes.number,
  body: PropTypes.string,
  onSuccess: PropTypes.func,
}
